/**
 * 统一设置页：外观 / 视频 / 音频 / 房间默认 / 本地数据。
 * 不依赖房间，任何路由下都可以从 TopBar 齿轮进入。
 */
import { useEffect, useState } from 'react';
import { TopBar } from '../app/TopBar.tsx';
import { useArena } from '../state/ArenaContext.tsx';
import {
  resetSettings,
  updateSettings,
  useSettings,
  type VideoFps,
} from '../state/settings.ts';
import { AudioDeviceLab } from '../components/system/AudioDeviceLab.tsx';
import { Badge, Button } from '../components/ui/primitives.tsx';

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint: string;
  children: React.ReactNode;
}) {
  return (
    <section className="lens p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-[14px] font-semibold tracking-wide text-slate-100">{title}</h2>
          <p className="mt-0.5 text-[11px] text-slate-500">{hint}</p>
        </div>
      </div>
      <div className="mt-3">{children}</div>
    </section>
  );
}

export default function SettingsPage() {
  const { state, actions } = useArena();
  const settings = useSettings();
  const [deviceLabOpen, setDeviceLabOpen] = useState(false);
  const [turnEnabled, setTurnEnabled] = useState<boolean | null>(null);
  useEffect(() => {
    let live = true;
    fetch('/health')
      .then((response) => response.json() as Promise<{ turn?: boolean }>)
      .then((data) => {
        if (live) setTurnEnabled(Boolean(data.turn));
      })
      .catch(() => {
        if (live) setTurnEnabled(null);
      });
    return () => {
      live = false;
    };
  }, []);
  const backRoute = state.self?.role === 'TEACHER' ? 'coach' : state.self && state.roomId ? 'arena' : 'home';

  return (
    <div className="min-h-screen" data-testid="settings-page">
      <TopBar compact />
      <main className="mx-auto max-w-[980px] space-y-3 px-5 py-6">
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="micro-label">SYSTEM SETTINGS</div>
            <h1 className="mt-1 text-[22px] font-semibold tracking-wide text-metal">统一设置</h1>
            <p className="mt-1 text-[11px] text-slate-500">
              外观、视频、音频、房间默认值集中在这里；设置只影响当前浏览器。
            </p>
          </div>
          <Button variant="secondary" size="sm" onClick={() => actions.setRoute(backRoute)} testId="settings-back">
            ← 返回
          </Button>
        </header>

        <Section title="矩阵背景" hint="直角坐标系 + 点阵；点阵随鼠标距离变化大小，保持极简制图台质感。">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex items-center justify-between gap-3 rounded-md bg-white/[0.025] px-3 py-2">
              <span className="text-[11px] text-slate-300">显示点阵</span>
              <input
                type="checkbox"
                data-testid="setting-bg-dots"
                checked={settings.backgroundDots}
                onChange={(event) => updateSettings({ backgroundDots: event.target.checked })}
                className="h-4 w-4 accent-white"
              />
            </label>
            <label className="rounded-md bg-white/[0.025] px-3 py-2">
              <div className="flex items-center justify-between gap-3">
                <span className="text-[11px] text-slate-300">点阵强度</span>
                <span className="mono text-[10px] text-slate-500">{settings.backgroundIntensity.toFixed(2)}×</span>
              </div>
              <input
                type="range"
                data-testid="setting-bg-intensity"
                min="0.35"
                max="1.8"
                step="0.05"
                value={settings.backgroundIntensity}
                onChange={(event) => updateSettings({ backgroundIntensity: Number(event.target.value) })}
                className="mt-2 w-full accent-white"
              />
            </label>
          </div>
        </Section>

        <Section title="视频" hint="帧率会在下一次启用媒体/切换摄像头时生效；远端视频会按姓名与辩位显示。">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="rounded-md bg-white/[0.025] px-3 py-2">
              <div className="text-[11px] text-slate-300">采集帧率</div>
              <select
                data-testid="setting-video-fps"
                value={settings.videoFps}
                onChange={(event) => updateSettings({ videoFps: Number(event.target.value) as VideoFps })}
                className="mt-2 w-full rounded border border-white/10 bg-black/30 px-2 py-1.5 text-[12px] text-slate-200"
              >
                {[15, 24, 30].map((fps) => (
                  <option key={fps} value={fps}>{fps} fps</option>
                ))}
              </select>
            </label>
            <label className="flex items-center justify-between gap-3 rounded-md bg-white/[0.025] px-3 py-2">
              <span className="text-[11px] text-slate-300">本地预览镜像</span>
              <input
                type="checkbox"
                data-testid="setting-mirror"
                checked={settings.mirrorLocalVideo}
                onChange={(event) => updateSettings({ mirrorLocalVideo: event.target.checked })}
                className="h-4 w-4 accent-white"
              />
            </label>
            <label className="flex items-center justify-between gap-3 rounded-md bg-white/[0.025] px-3 py-2">
              <span className="text-[11px] text-slate-300">默认开启摄像头</span>
              <input
                type="checkbox"
                data-testid="setting-camera-default"
                checked={settings.cameraDefault}
                onChange={(event) => updateSettings({ cameraDefault: event.target.checked })}
                className="h-4 w-4 accent-white"
              />
            </label>
            <div className="rounded-md bg-white/[0.025] px-3 py-2 text-[10px] leading-relaxed text-slate-500">
              视频墙在教师 Live 与辩手 HUD 顶部；未开摄像头时显示姓名/辩位占位，不会黑块。
            </div>
          </div>
        </Section>

        <Section title="音频设备" hint="麦克风/扬声器枚举、输入电平、输出测试音；选择会用于下一次采集/播放。">
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="secondary" onClick={() => setDeviceLabOpen(true)} testId="settings-device-lab">
              打开设备自检
            </Button>
            <Badge tone="neutral">输入：{settings.audioInputId ? '已选择' : '系统默认'}</Badge>
            <Badge tone="neutral">输出：{settings.audioOutputId ? '已选择' : '系统默认'}</Badge>
            <Badge tone={turnEnabled ? 'pass' : 'warning'}>
              TURN：{turnEnabled === null ? '检测中' : turnEnabled ? '已配置' : '仅 STUN'}
            </Badge>
          </div>
        </Section>

        <Section title="房间默认值" hint="创建房间弹窗会读取这里的容量与媒体策略；信令层仍以服务端配置为最终上限。">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="rounded-md bg-white/[0.025] px-3 py-2">
              <div className="text-[11px] text-slate-300">默认容量</div>
              <select
                data-testid="setting-room-capacity"
                value={settings.roomCapacity}
                onChange={(event) => updateSettings({ roomCapacity: Number(event.target.value) as 4 | 8 | 12 })}
                className="mt-2 w-full rounded border border-white/10 bg-black/30 px-2 py-1.5 text-[12px] text-slate-200"
              >
                {[4, 8, 12].map((capacity) => (
                  <option key={capacity} value={capacity}>{capacity} 人</option>
                ))}
              </select>
            </label>
            <label className="rounded-md bg-white/[0.025] px-3 py-2">
              <div className="text-[11px] text-slate-300">默认媒体策略</div>
              <select
                data-testid="setting-media-policy"
                value={settings.mediaPolicy}
                onChange={(event) => updateSettings({ mediaPolicy: event.target.value as 'audio-only' | 'audio-video' })}
                className="mt-2 w-full rounded border border-white/10 bg-black/30 px-2 py-1.5 text-[12px] text-slate-200"
              >
                <option value="audio-video">音视频</option>
                <option value="audio-only">仅音频</option>
              </select>
            </label>
          </div>
        </Section>

        <Section title="本地数据" hint="设置与便签存在 localStorage；清除后不会影响已上传代码或房间服务器。">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              testId="settings-reset"
              onClick={() => resetSettings()}
            >
              恢复默认设置
            </Button>
            <Button size="sm" variant="secondary" onClick={() => actions.setRoute(backRoute)}>
              返回当前页面
            </Button>
          </div>
        </Section>
      </main>
      {deviceLabOpen && <AudioDeviceLab onClose={() => setDeviceLabOpen(false)} />}
    </div>
  );
}
